<script setup lang="ts">
import { computed, onBeforeMount, toRefs } from 'vue'

import { useRouter, useRoute } from 'vue-router'

import { Icon } from '@iconify/vue/dist/iconify.js'

import { forceLogout } from '@/utils/auth/forceLogout'
import { redirectToFrontend } from '@/utils/common/redirectToFrontend'

import { SystemUserRole } from '@/api-generator/aggr/api'
import { AvatarSize } from '@/constant/user'
import appStore from '@/stores'

const router = useRouter()
const route = useRoute()

const { hasUserInfo, getUserInfo, handlePermission, setUserBlobAvatar } = appStore.useAuthStore
const { userInfo } = toRefs(appStore.useAuthStore)

interface routerOption {
  name?: string
}

interface MenuItem {
  name: string
  icon: string
  label: string
  permissions: SystemUserRole[]
}

interface MenuGroup {
  title: string
  titlePermissions: SystemUserRole[]
  items: MenuItem[]
}

const menuGroups: MenuGroup[] = [
  {
    title: '',
    titlePermissions: [],
    items: [
      {
        name: 'tag-manage',
        icon: 'ri:bookmark-line',
        label: '受眾標籤',
        permissions: [SystemUserRole.Admin, SystemUserRole.SuperUser]
      },
      {
        name: 'company-manage',
        icon: 'ri:building-line',
        label: '公司列表',
        permissions: [SystemUserRole.Admin, SystemUserRole.SuperUser]
      },
      {
        name: 'organization-manage',
        icon: 'ri:user-community-line',
        label: '組織列表',
        permissions: [SystemUserRole.Admin, SystemUserRole.SuperUser]
      },
      {
        name: 'user-manage',
        icon: 'ri:file-list-3-line',
        label: '人員列表',
        permissions: [SystemUserRole.Admin, SystemUserRole.SuperUser]
      },
      { name: 'admin-manage', icon: 'ri:computer-line', label: '系統管理', permissions: [SystemUserRole.Admin] }
    ]
  },
  {
    title: '管理',
    titlePermissions: [SystemUserRole.Guest],
    items: [
      {
        name: 'organization-manage',
        icon: 'ri:user-community-line',
        label: '組織列表',
        permissions: [SystemUserRole.Guest]
      },
      { name: 'user-manage', icon: 'ri-file-list-3-line', label: '人員列表', permissions: [SystemUserRole.Guest] }
    ]
  },
  {
    title: '訂閱管理',
    titlePermissions: [SystemUserRole.Guest],
    items: [{ name: 'billing', icon: 'ri-wallet-line', label: '帳單', permissions: [SystemUserRole.Guest] }]
  }
]

const goto = async (option: routerOption) => {
  if (option.name) {
    if (option.name === 'company-switch') {
      await redirectToFrontend(router)
    } else {
      router.push({ name: option.name })
    }
  }
}

const visibleMenuGroups = computed(() =>
  menuGroups.filter((group) => group.items.some((item) => handlePermission(item.permissions)))
)

const activeRoute = computed(() => {
  const routeNames = menuGroups.flatMap((group) => group.items.map((item) => item.name))
  return routeNames.find((name) => route.matched.some((record) => record.name === name)) ?? ''
})

const logout = async () => {
  await forceLogout()
}

const handleUserInfoPermission = async () => {
  if (!hasUserInfo()) {
    await getUserInfo()
  }

  const role = userInfo.value?.role
  const isAdminOrSuperUser = role === SystemUserRole.Admin || role === SystemUserRole.SuperUser
  const isCompanyOwner = role === SystemUserRole.Guest && userInfo.value?.is_company_owner === true

  if (!isAdminOrSuperUser && !isCompanyOwner) {
    router.push({ name: 'frontend' })
    return
  }

  if (route.name === 'admin') {
    router.push({ name: isAdminOrSuperUser ? 'tag-manage' : 'organization-manage' })
  }
}

onBeforeMount(async () => {
  handleUserInfoPermission()
  await setUserBlobAvatar(AvatarSize.SMALL)
})
</script>

<template>
  <div class="admin-view">
    <aside class="admin-sidebar">
      <div class="flex items-center justify-center border-b-[1.5px] border-solid border-b-app-black-75">
        <button
          type="button"
          class="flex h-[54px] cursor-pointer items-center justify-center"
          aria-label="回到首頁"
          @click="goto({ name: 'company-switch' })"
        >
          <span class="hidden md:block">
            <SVGIcon name="admin-logo-icon" width="115px" height="23px" />
          </span>
          <Icon icon="ri:home-4-line" class="md:hidden" width="20px" height="20px" />
        </button>
      </div>
      <div class="flex h-[calc(100vh-56px)] flex-col justify-between px-2 pb-[30px] pt-8 md:px-4">
        <div>
          <div class="flex flex-col gap-8">
            <div v-for="(group, groupIndex) in visibleMenuGroups" :key="groupIndex">
              <h3 v-if="group.title && handlePermission(group.titlePermissions)" class="menu-title">
                {{ group.title }}
              </h3>
              <template v-for="item in group.items" :key="item.name">
                <div
                  v-if="handlePermission(item.permissions)"
                  class="admin-link"
                  :class="{ active: activeRoute === item.name }"
                  @click="goto({ name: item.name })"
                >
                  <Icon :icon="item.icon" width="14px" height="14px" />
                  <span class="hidden md:inline">{{ item.label }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div>
          <button type="button" class="navigation-link" @click="goto({ name: 'company-switch' })">
            <span class="hidden md:inline">前台連結</span>
            <Icon icon="ri:arrow-right-up-long-line" width="14px" height="14px" />
          </button>
          <button type="button" class="navigation-link" @click="logout">
            <Icon icon="ri:logout-box-r-line" class="md:hidden" width="14px" height="14px" />
            <span class="hidden md:inline">登出</span>
          </button>
        </div>
      </div>
    </aside>
    <div class="content">
      <RouterView />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.admin-view {
  display: flex;
  .content {
    flex: 1;
    min-width: 0;
    height: 100vh;
  }
}

.admin-sidebar {
  @apply h-screen w-16 shrink-0 md:w-[275px];
  background: linear-gradient(180deg, #eef1f8 48%, rgba(238, 241, 248, 0.4) 147.06%);
}

.menu-title {
  @apply mb-2 hidden pl-9 text-xs font-normal tracking-[1px] text-app-black-500 md:block;
}

.admin-link {
  @apply flex cursor-pointer items-center justify-center gap-[7px] rounded-lg px-0 py-2.5 text-sm font-semibold leading-normal text-app-black-900 hover:bg-app-primary-blue-400 hover:text-white md:justify-start md:px-9;

  &.active {
    @apply bg-app-primary-blue-400 text-white;
  }
}

.navigation-link {
  @apply flex w-full cursor-pointer items-center justify-center gap-1 rounded-lg px-0 py-2.5 text-left text-sm font-semibold text-app-black-650 hover:bg-app-primary-blue-400 hover:text-white md:justify-start md:px-9;
}
</style>
